import { ArrowUpRight } from "lucide-react";
import { SERVICES } from "#/lib/constants";
import { img, PHOTO } from "#/lib/images";
import { CTAButton } from "#/components/shared/CTAButton";
import { SectionLabel } from "#/components/shared/SectionLabel";
import { useScrollAnimation } from "#/hooks/useScrollAnimation";

/**
 * Section 3 — compact services teaser (full detail lives on /services, an
 * icon-card grid with no photos, and on each per-service page). Left: label,
 * headline, photo, CTA. Right: the 8 services, each linking to its per-service
 * page.
 */
export function ServicesTeaser() {
  const { ref, isVisible } = useScrollAnimation();

  return (
    <section id="services" className="bg-white py-20 lg:py-28">
      <div
        ref={ref}
        className={`mx-auto grid max-w-7xl gap-12 px-4 transition-all duration-700 motion-reduce:transition-none sm:px-6 lg:grid-cols-[1fr_1.1fr] lg:gap-16 lg:px-8 ${
          isVisible ? "translate-y-0 opacity-100" : "translate-y-6 opacity-0"
        }`}
      >
        <div className="flex flex-col items-start">
          <SectionLabel>Our Services</SectionLabel>
          <h2 className="mt-3 font-heading text-3xl font-black uppercase tracking-tight text-graphite sm:text-4xl lg:text-5xl">
            Every kind of concrete work, one crew
          </h2>
          <p className="mt-4 max-w-md text-[15px] leading-relaxed text-graphite/70">
            Eight core services, from new driveways to epoxy floors, all poured on a
            proper sub-base and backed by a written workmanship warranty.
          </p>
          <img
            src={img(PHOTO.driveway, { w: 900, h: 600 })}
            alt="Fresh residential concrete driveway"
            width={900}
            height={600}
            loading="lazy"
            decoding="async"
            className="mt-8 aspect-[3/2] w-full rounded-2xl object-cover shadow-xl"
          />
          <CTAButton href="/services" variant="flame" className="mt-8 uppercase tracking-wide">
            View all services
          </CTAButton>
        </div>

        <nav aria-label="Our services" className="flex flex-col justify-center">
          {SERVICES.map((service, i) => (
            <a
              key={service.slug}
              href={`/services#${service.slug}`}
              className="group flex items-center justify-between gap-4 border-b border-graphite/10 py-4 no-underline first:border-t lg:py-5"
            >
              <span className="flex items-baseline gap-4">
                <span className="font-heading text-xs font-bold text-flame">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <span className="font-heading text-lg font-bold uppercase tracking-tight text-graphite transition-colors group-hover:text-flame sm:text-xl">
                  {service.title}
                </span>
              </span>
              <ArrowUpRight className="h-5 w-5 shrink-0 text-graphite/30 transition-all group-hover:translate-x-0.5 group-hover:text-flame" />
            </a>
          ))}
        </nav>
      </div>
    </section>
  );
}
